import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InfoBanner, SuccessBanner } from '../../../shared/components/Feedback';
import { curriculumAPI } from '../services/api';

/**
 * PreTestBanner — offers learners a chance to test out of a module.
 *
 * Props:
 *   domain         (string)      — domain ID
 *   moduleId       (string)      — module ID
 *   moduleTitle    (string)      — display name of the module
 *   quizSegmentId  (string|null) — if set, navigate to that quiz segment
 *   tutorialId     (string|null) — tutorial containing the quiz segment
 *   trackId        (string|null) — track context for navigation state
 *   onSkipped      (fn)          — called after the learner passes and skips
 *   onDismissed    (fn)          — called when the learner dismisses the banner
 */
export default function PreTestBanner({
  domain,
  moduleId,
  moduleTitle,
  quizSegmentId,
  tutorialId,
  trackId,
  onSkipped,
  onDismissed,
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle'); // idle | loading | success | failed | hidden
  const [resultMsg, setResultMsg] = useState('');

  useEffect(() => {
    // Check whether the learner already attempted this pre-test.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await curriculumAPI.getPretestStatus(domain, moduleId);
        if (!cancelled && data.attempted) {
          setStatus('hidden');
        }
      } catch {
        // Fail silently — show the banner by default.
      }
    })();
    return () => { cancelled = true; };
  }, [domain, moduleId]);

  const handleStartPreTest = () => {
    if (quizSegmentId && tutorialId) {
      // Navigate to the TutorialPlayerPage for just this tutorial (the quiz).
      navigate(`/tutorials/${domain}/play/${tutorialId}`, {
        state: {
          track_id: trackId,
          module_id: moduleId,
          tutorial_id: tutorialId,
          pretest_mode: true,
          quiz_segment_id: quizSegmentId,
          pretest_domain: domain,
          pretest_module_id: moduleId,
          pretest_on_complete_redirect: window.location.pathname,
        },
      });
    } else {
      // No quiz segment available — nothing to navigate to.
      onDismissed?.();
    }
  };

  // Allow external completion via postMessage (from TutorialPlayerPage in pretest_mode).
  useEffect(() => {
    const handler = async (event) => {
      if (!event.data || event.data.type !== 'PRETEST_COMPLETE') return;
      if (event.data.moduleId !== moduleId) return;

      const { score, totalQuestions, correct } = event.data;
      try {
        setStatus('loading');
        const { data } = await curriculumAPI.completePretest(domain, moduleId, {
          score,
          total_questions: totalQuestions,
          correct,
        });
        if (data.passed) {
          setStatus('success');
          setResultMsg(data.message || 'You passed! Module marked complete.');
          setTimeout(() => onSkipped?.(), 2000);
        } else {
          setStatus('failed');
          setResultMsg(data.message || "Keep going — work through the lessons to master this topic.");
          setTimeout(() => onDismissed?.(), 2500);
        }
      } catch {
        setStatus('hidden');
        onDismissed?.();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [domain, moduleId, onSkipped, onDismissed]);

  if (status === 'hidden') return null;

  if (status === 'success') {
    return (
      <SuccessBanner
        msg={`🚀 ${resultMsg}`}
        onDismiss={() => { setStatus('hidden'); onSkipped?.(); }}
      />
    );
  }

  if (status === 'failed') {
    return (
      <InfoBanner
        title="No worries — let's study!"
        message={resultMsg}
        dismissible
        onDismiss={() => { setStatus('hidden'); onDismissed?.(); }}
      />
    );
  }

  // Default: offer pre-test.
  return (
    <InfoBanner
      title="Test out of this module?"
      message={
        status === 'loading'
          ? 'Loading pre-test…'
          : `If you already know ${moduleTitle || 'this topic'}, take a quick quiz to skip the lessons and earn credit.`
      }
      dismissible
      onDismiss={() => { setStatus('hidden'); onDismissed?.(); }}
      action={
        status !== 'loading'
          ? { label: 'Take Pre-Test →', onClick: handleStartPreTest }
          : undefined
      }
    />
  );
}
